import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { isWeeklyOrMonthlyOrTrimesterOrYearlyTrigger } from 'modules/automations/lib/isWeeklyOrMonthlyTrigger'
import { automations } from 'modules/automations/store/collections/automations'
import type { Automation } from 'modules/automations/store/models/Automation'
import { Trigger } from 'modules/automations/store/models/Trigger'

import Monthly from './Monthly'
import Trimester from './Trimester'
import Weekly from './Weekly'
import Yearly from './Yearly'

export interface Props {
  automationId: string
  onClose: () => void
}

const EditDateAutomationModal = ({ automationId, onClose }: Props) => {
  const { navigation } = useNavigation()

  const navigate = useNavigate()
  const [day, setDay] = React.useState<number>()

  const automation = useMemo<Automation>(() => {
    const automation = automations.find({
      id: automationId,
    })

    if (!automation) {
      throw new Error(`Missing automation with id: ${automationId}`)
    }

    return automation
  }, [automationId])

  const [selectedMonthday, setSelectedMonthday] = React.useState(automation.attributes.repeat_data)

  useEffect(() => {
    if (!isWeeklyOrMonthlyOrTrimesterOrYearlyTrigger(automation.attributes.trigger)) {
      navigate(navigation.settings.automations.id.fullPath({ id: automationId }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automation.attributes.trigger])

  const onSubmit = async (closePortal: ClosePortalFn) => {
    if (!automation) return

    const { success } = await asyncAction(() =>
      automation.save({
        repeat_data:
          Trigger.ContentKinds.yearly === automation.attributes.trigger ? selectedMonthday : day,
      })
    )

    if (success) closePortal()
  }

  return (
    <>
      {automation && isWeeklyOrMonthlyOrTrimesterOrYearlyTrigger(automation.attributes.trigger) && (
        <Modal
          illustration="calendar"
          title={i18n.t('automations.automation.edit_date.title')}
          description={i18n.t('automations.automation.edit_date.description')}
          onSubmit={onSubmit}
          onClose={onClose}
          footer={<Button submit>{i18n.t('common.save')}</Button>}
        >
          {Trigger.ContentKinds.monthly === automation.attributes.trigger && (
            <Monthly
              automation={automation}
              setDay={setDay}
              selectedMonthday={selectedMonthday}
              setSelectedMonthday={setSelectedMonthday}
            />
          )}
          {Trigger.ContentKinds.trimester === automation.attributes.trigger && (
            <Trimester
              automation={automation}
              selectedMonthday={selectedMonthday}
              setSelectedMonthday={setSelectedMonthday}
            />
          )}
          {Trigger.ContentKinds.weekly === automation.attributes.trigger && (
            <Weekly automation={automation} setDay={setDay} />
          )}
          {Trigger.ContentKinds.yearly === automation.attributes.trigger && (
            <Yearly
              automation={automation}
              selectedMonthday={selectedMonthday}
              setSelectedMonthday={setSelectedMonthday}
            />
          )}
        </Modal>
      )}
    </>
  )
}

export default EditDateAutomationModal
